import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Drama } from "@/types/drama";

interface HeroSliderProps {
  slides: Drama[];
}

export default function HeroSlider({ slides }: HeroSliderProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (slides.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);

  if (slides.length === 0) return null;
  const current = slides[active];

  return (
    <section className="relative h-[520px] w-full overflow-hidden bg-primary-950 md:h-[600px] lg:h-[620px]">
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === active ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden={index !== active}
        >
          <img
            src={slide.banner}
            alt={`${slide.title} 剧照`}
            title={`${slide.title} 高清电视剧在线观看`}
            className={`h-full w-full object-cover object-top ${index === active ? "animate-slow-zoom" : ""}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-transparent to-primary-950/50" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full flex-col justify-end px-4 pb-10 md:px-8 md:pb-14 lg:pb-16">
        <div className="max-w-2xl">
          <h1 className="mb-3 text-[11px] font-medium tracking-[0.3em] text-background-300 md:text-xs">
            嫂子的职业在线观看 · 高清剧集
          </h1>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-[11px] font-semibold text-primary-950">
              <i className="ri-fire-line" aria-hidden="true" />
              热播推荐
            </span>
            <span className="rounded-full border border-background-50/25 px-3 py-1 text-[11px] text-background-200">
              {current.status}
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] text-accent-300">
              <i className="ri-star-fill" aria-hidden="true" />
              {current.rating.toFixed(1)} 分
            </span>
          </div>

          <h2 key={current.id} className="animate-fade-up mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl lg:text-[56px]">
            {current.title}
          </h2>

          <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-background-200 md:text-sm">
            <span>{current.year}</span>
            <span className="text-background-500">|</span>
            <span>{current.region}</span>
            <span className="text-background-500">|</span>
            <span>{current.genres.join(" · ")}</span>
            <span className="text-background-500">|</span>
            <span>主演 {current.cast.slice(0, 2).join("、")}</span>
          </p>

          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-300 sm:line-clamp-3 md:block">
            {current.synopsis}
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-base" aria-hidden="true" />
              立即观看
            </Link>
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:border-background-50/70 hover:bg-background-50/10"
            >
              剧集详情
              <i className="ri-arrow-right-line" aria-hidden="true" />
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActive(index)}
                aria-label={`切换到 ${slide.title}`}
                aria-current={index === active}
                className={`h-1 cursor-pointer rounded-full transition-all ${
                  index === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
            <span className="ml-2 text-[11px] tabular-nums text-background-300">
              0{active + 1} / 0{slides.length}
            </span>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setActive(index)}
                className={`group flex w-32 cursor-pointer items-center gap-2 rounded-md border p-1.5 text-left transition-colors ${
                  index === active
                    ? "border-accent-500/70 bg-primary-950/60"
                    : "border-background-50/15 bg-primary-950/30 hover:border-background-50/40"
                }`}
              >
                <span className="h-11 w-8 shrink-0 overflow-hidden rounded">
                  <img
                    src={slide.cover}
                    alt={slide.title}
                    className="h-full w-full object-cover object-top"
                  />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[11px] font-medium text-background-50">
                    {slide.title}
                  </span>
                  <span className="block truncate text-[10px] text-background-400">{slide.status}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}